import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api, saveSession } from "@/lib/api";
import { Loader2, ChevronDown, ChevronUp } from "lucide-react";
import BuildBadge from "@/components/BuildBadge";
import PublicRadioPanel from "@/components/PublicRadioPanel";
import EasterEggSlot from "@/components/EasterEggSlot";
import { useUtmCapture } from "@/hooks/useUtmCapture";

export default function Login() {
    const location = useLocation();
    // Iter 31.237 — anonymous UTM capture on landing. Fires once per
    // session per URL. No-op for organic visits.
    useUtmCapture(location.pathname);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);
    const [showClearance, setShowClearance] = useState(false);
    // Iter 31.93 — surface LinkedIn config status so the operator
    // sees "MISSING SECRET" instead of clicking through to a generic
    // "malformed" error on LinkedIn's end.
    const [linkedinStatus, setLinkedinStatus] = useState(null);
    useEffect(() => {
        api.get("/oauth/linkedin/status")
            .then(({ data }) => setLinkedinStatus(data))
            .catch(() => setLinkedinStatus({ configured: false, diagnosis: "Status check failed." }));
    }, []);
    const nav = useNavigate();

    // Iter 31.211 — test-mode signup state.
    // Iter 31.237 — when landed via /register path, open signup panel by default.
    const [signupOpen, setSignupOpen] = useState(location.pathname === "/register");
    // Iter 31.212 — demo CTA state.
    const [demoStatus, setDemoStatus] = useState(null);
    const [demoLoading, setDemoLoading] = useState(false);
    useEffect(() => {
        api.get("/demo/status").then(({ data }) => setDemoStatus(data)).catch(() => {});
    }, []);
    const tryDemo = async () => {
        if (!demoStatus?.enabled || demoLoading) return;
        setDemoLoading(true);
        try {
            const { data } = await api.post("/auth/login", {
                email: demoStatus.email,
                password: demoStatus.password_hint,
            });
            // Iter 31.252 — saveSession takes (token, user), not (data).
            // The bug was storing the whole response object as the token
            // string, then every subsequent API call sent
            // `Authorization: Bearer [object Object]` → 401 → interceptor
            // logged the user out instantly. "throws me straight out."
            saveSession(data.token, data.user);
            nav("/");
        } catch (e) {
            setDemoLoading(false);
        }
    };
    const [su, setSu] = useState({ email: "", password: "", name: "", intent: "test_user" });
    const [suBusy, setSuBusy] = useState(false);
    const [suErr, setSuErr] = useState("");
    // Iter 31.211a — probe backend so the signup button only renders when
    // TEST_MODE=1 is actually set on the deployed container. Stops the
    // operator clicking a button that the server will reject anyway.
    const [testSignupEnabled, setTestSignupEnabled] = useState(null);
    useEffect(() => {
        api.get("/auth/test-signup-status")
            .then(({ data }) => setTestSignupEnabled(!!data?.enabled))
            .catch(() => setTestSignupEnabled(false));
    }, []);
    const submitSignup = async (e) => {
        e.preventDefault();
        setSuErr("");
        if (su.password.length < 8) { setSuErr("Password must be at least 8 characters."); return; }
        setSuBusy(true);
        try {
            const { data } = await api.post("/auth/test-signup", su);
            saveSession(data.token, data.user);
            nav("/");
        } catch (e) {
            setSuErr(e?.response?.data?.detail || e.message);
        } finally {
            setSuBusy(false);
        }
    };

    // Iter 30 (19 Feb 2026): Emergent-managed Google Sign-In.
    // After Google auth, the user lands at /login#session_id=<id>.
    // We detect it synchronously here, exchange it server-side for our JWT,
    // save the session, and bounce to "/".
    useEffect(() => {
        const hash = window.location.hash || "";

        // ----- LinkedIn OIDC callback (iter 31.11, 22 May 2026) -----
        // Backend redirects to /login#token=<jwt>&user=<json>&linkedin_connected=1
        // on success, or /login#linkedin_error=<msg> on failure.
        if (hash.includes("linkedin_error=")) {
            const m = hash.match(/linkedin_error=([^&]+)/);
            window.history.replaceState({}, "", "/login");
            if (m) setErr(`LinkedIn sign-in failed: ${decodeURIComponent(m[1])}`);
            return;
        }
        if (hash.includes("linkedin_connected=1")) {
            const tokMatch = hash.match(/token=([^&]+)/);
            const userMatch = hash.match(/user=([^&]+)/);
            window.history.replaceState({}, "", "/login");
            if (tokMatch && userMatch) {
                try {
                    const tok = decodeURIComponent(tokMatch[1]);
                    const usr = JSON.parse(decodeURIComponent(userMatch[1]));
                    saveSession(tok, usr);
                    try {
                        const created = usr?.created_at ? new Date(usr.created_at).getTime() : 0;
                        const isNew = usr?.created_via === "linkedin_oauth" && (Date.now() - created) < 5 * 60 * 1000;
                        localStorage.setItem("shed_greeting", JSON.stringify({
                            name: usr?.name,
                            picture: usr?.linkedin_picture || null,
                            is_new: isNew,
                            at: Date.now(),
                        }));
                    } catch (_) { /* non-fatal */ }
                    nav("/");
                    return;
                } catch (e) {
                    setErr("LinkedIn sign-in succeeded but the response was malformed.");
                    return;
                }
            }
        }

        if (!hash.includes("session_id=")) return;
        const m = hash.match(/session_id=([^&]+)/);
        if (!m) return;
        const sid = decodeURIComponent(m[1]);
        // Strip the fragment immediately so a reload doesn't re-trigger the exchange
        window.history.replaceState({}, "", "/login");
        setGoogleLoading(true);
        (async () => {
            try {
                const { data } = await api.post("/oauth/google/exchange", { session_id: sid });
                saveSession(data.token, data.user);
                try {
                    const created = data.user?.created_at ? new Date(data.user.created_at).getTime() : 0;
                    const isNew = data.user?.created_via === "google_oauth" && (Date.now() - created) < 5 * 60 * 1000;
                    localStorage.setItem("shed_greeting", JSON.stringify({
                        name: data.user?.name,
                        picture: data.user?.google_picture || null,
                        is_new: isNew,
                        at: Date.now(),
                    }));
                } catch (_) { /* non-fatal */ }
                nav("/");
            } catch (e2) {
                setErr(
                    e2?.response?.data?.detail?.toString() ||
                    "Google sign-in failed. Try again or use email + clearance code.",
                );
                setGoogleLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        try {
            const { data } = await api.post("/auth/login", { email, password });
            saveSession(data.token, data.user);
            nav("/");
        } catch (err2) {
            setErr(err2.response?.data?.detail?.toString() || "Not on the list. Try again.");
        } finally {
            setLoading(false);
        }
    };

    const signInWithGoogle = () => {
        setErr("");
        setGoogleLoading(true);
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/login";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    return (
        <div className="min-h-screen relative flex items-center justify-center px-4 py-12" data-testid="login-page">
            <div className="coffee-ring" style={{ top: "15vh", left: "12vw" }} aria-hidden />
            <div className="coffee-ring" style={{ bottom: "12vh", right: "10vw", width: 180, height: 180, opacity: 0.4 }} aria-hidden />

            {/* Iter 31.211 — side-by-side: auth box LEFT, public radio RIGHT.
                Stacks on mobile (lg break). Centred container holds both.
                The previously max-w-lg auth card stays at the same width;
                we just sit a parallel radio card next to it. */}
            <div className="relative w-full max-w-5xl flex flex-col lg:flex-row items-center justify-center gap-10">
            <div
                className="paper-card tape-corner relative w-full max-w-lg px-10 pt-12 pb-10 tilt-l"
                data-testid="login-form"
            >
                <div
                    className="absolute -top-3 -right-6 classified-stamp stamp-in"
                    data-testid="login-classified-stamp"
                >
                    Classified
                </div>

                <div className="mb-2">
                    <div className="label-typed">File · Ref BJLIAM / 06</div>
                    <h1 className="font-heading text-5xl uppercase tracking-[0.05em] text-ink leading-none mt-2">
                        Spy Finance
                        <span className="text-stamp-red text-2xl ml-2">●</span>
                    </h1>
                    <div className="font-body text-[11px] tracking-[0.2em] text-ink-soft uppercase mt-2">
                        Build Engine · Agent Three · Twenty-year horizon
                    </div>
                    <div
                        className="font-body text-[10px] tracking-[0.22em] text-stamp-red uppercase mt-1.5 flex items-center gap-1.5"
                        data-testid="login-brand-domain"
                    >
                        <span className="inline-block w-1 h-1 bg-stamp-red rounded-full" aria-hidden />
                        Spyfinance.cloud · Bridge of Continuity
                    </div>
                </div>

                <div className="hairline my-6" />

                <div className="mb-6 font-body text-sm text-ink leading-relaxed">
                    <p>
                        <span className="pencil text-lg mr-1">No. 6,</span>
                    </p>
                    <p className="mt-3">
                        You've walked back through the door. Sign in below and resume
                        the paperwork. I'll be watching.
                    </p>
                    <p className="mt-3 text-ink-soft italic">— No. 2</p>
                </div>

                {err && (
                    <div
                        className="stamp-red border border-stamp-red text-[11px] tracking-[0.15em] px-3 py-2 mb-4"
                        data-testid="login-error"
                    >
                        {err}
                    </div>
                )}

                {/* Primary auth — Google. One tap, no env-var setup. */}
                <button
                    type="button"
                    onClick={signInWithGoogle}
                    disabled={googleLoading || loading}
                    className="w-full bg-ink text-paper-light font-heading uppercase tracking-[0.22em] text-sm py-4 hover:bg-stamp-red transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-3"
                    data-testid="login-google-button"
                    aria-label="Sign in with Google"
                >
                    {googleLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 18 18"
                            className="w-4 h-4 shrink-0"
                            aria-hidden="true"
                        >
                            <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
                            <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
                            <path fill="#FBBC05" d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z"/>
                            <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.167 6.656 3.58 9 3.58z"/>
                        </svg>
                    )}
                    {googleLoading ? "Redirecting..." : "Sign in with Google"}
                </button>

                <p className="font-body text-[11px] text-ink-soft mt-2 leading-relaxed">
                    One tap. No new password to remember. Your Google identity is
                    verified by Emergent; we only see your name, email, and avatar.
                </p>

                {/* LinkedIn Sign-In — for invited operatives (iter 31.11, 22 May 2026). */}
                {linkedinStatus && !linkedinStatus.configured ? (
                    <div
                        className="w-full mt-4 border-2 border-stamp-red/40 bg-stamp-red/5 p-3 text-center"
                        data-testid="login-linkedin-disabled"
                    >
                        <div className="font-heading uppercase tracking-[0.22em] text-[10px] text-stamp-red mb-1">
                            LinkedIn unavailable
                        </div>
                        <div className="font-body text-[11px] text-ink-soft">
                            {linkedinStatus.diagnosis || "LinkedIn sign-in is not configured on this deployment."}
                        </div>
                    </div>
                ) : (
                    <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/oauth/linkedin/login`}
                        className="w-full mt-4 border-2 border-ink/30 text-ink font-heading uppercase tracking-[0.22em] text-sm py-4 hover:border-stamp-red hover:text-stamp-red transition-colors inline-flex items-center justify-center gap-3"
                        data-testid="login-linkedin-button"
                        aria-label="Sign in with LinkedIn"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            className="w-4 h-4 shrink-0"
                            aria-hidden="true"
                            fill="currentColor"
                        >
                            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.024-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.063 2.063 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                        </svg>
                        Sign in with LinkedIn
                    </a>
                )}

                <p className="font-body text-[11px] text-ink-soft mt-2 leading-relaxed">
                    For invited operatives. Your LinkedIn identity links you to whatever historical
                    data the Shed already has filed under your name.
                </p>

                {/* Secondary — email + clearance code, collapsed by default. */}
                <button
                    type="button"
                    onClick={() => setShowClearance((v) => !v)}
                    className="w-full flex items-center justify-between mt-8 pt-4 border-t border-ink/15 text-[10px] font-body text-ink-soft hover:text-stamp-red uppercase tracking-[0.22em] transition-colors"
                    data-testid="login-clearance-toggle"
                    aria-expanded={showClearance}
                >
                    <span>Use clearance code instead</span>
                    {showClearance ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </button>

                {showClearance && (
                    <form onSubmit={submit} className="mt-4" data-testid="login-clearance-form">
                        {/* Iter 31.215 — atmospheric whisper above the email field. */}
                        <EasterEggSlot
                            path="login"
                            slot="login.above_form_whisper"
                            as="div"
                            className="mb-3 text-[10px] font-body text-ink-soft italic tracking-[0.12em] opacity-60"
                        />
                        <label className="label-typed block mb-1">Operative email</label>
                        <input
                            type="text"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            autoFocus
                            placeholder="no6@spyfinance.cloud"
                            autoComplete="username"
                            className="w-full px-3 py-2 mb-4 font-body text-sm"
                            data-testid="login-email-input"
                        />

                        <label className="label-typed block mb-1">Clearance code</label>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            placeholder="••••••••"
                            className="w-full px-3 py-2 mb-4 font-body text-sm"
                            data-testid="login-password-input"
                        />

                        <button
                            type="submit"
                            disabled={loading || googleLoading}
                            className="w-full bg-paper-light text-ink font-heading uppercase tracking-[0.3em] text-sm py-3 border-2 border-ink/40 hover:border-stamp-red hover:text-stamp-red transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
                            data-testid="login-submit-button"
                        >
                            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                            {loading ? "Cross-checking..." : "Be Seeing You"}
                        </button>
                    </form>
                )}

                {/* Iter 31.211a — test-mode header + signup button only
                    render when backend reports TEST_MODE=1. Stops the
                    operator clicking a button the server rejects. */}
                {testSignupEnabled && (
                    <div className="mt-8 label-typed leading-relaxed">
                        <div>● Test mode — open registration for test/launch operatives.</div>
                    </div>
                )}
                {/* Iter 31.211 — test-mode signup. Collapsible to keep the
                    primary login form uncluttered. */}
                {testSignupEnabled && (
                <div className="mt-4">
                    {!signupOpen ? (
                        <>
                            <button
                                type="button"
                                onClick={() => setSignupOpen(true)}
                                data-testid="login-signup-toggle"
                                className="w-full border border-dashed border-stamp-red/60 text-stamp-red font-heading uppercase tracking-[0.22em] text-xs py-2 hover:bg-stamp-red hover:text-paper-light"
                            >
                                Register as test operative →
                            </button>
                            {/* Iter 31.212 — demo account CTA. One-tap entry,
                                read-only, auto-resets nightly. */}
                            {demoStatus?.enabled && (
                                <button
                                    type="button"
                                    onClick={tryDemo}
                                    disabled={demoLoading}
                                    data-testid="login-try-demo"
                                    className="w-full mt-2 border border-ink/40 text-ink font-heading uppercase tracking-[0.22em] text-xs py-2 hover:bg-ink hover:text-paper-light disabled:opacity-50"
                                >
                                    {demoLoading ? "Entering demo..." : "Try the demo (read-only) →"}
                                </button>
                            )}
                        </>
                    ) : (
                        <form onSubmit={submitSignup} className="space-y-3 border border-ink/15 p-4 bg-paper-light/60" data-testid="login-signup-form">
                            <div className="label-typed text-stamp-red">New operative registration</div>
                            <div>
                                <label className="label-typed text-[10px] tracking-[0.2em] text-ink-soft">Codename / display name</label>
                                <input
                                    type="text"
                                    value={su.name}
                                    onChange={(e) => setSu({ ...su, name: e.target.value })}
                                    required maxLength={80}
                                    placeholder="e.g. Number Twelve"
                                    className="w-full border border-ink/30 bg-paper-light px-3 py-2 font-body text-sm text-ink"
                                    data-testid="signup-name"
                                />
                            </div>
                            <div>
                                <label className="label-typed text-[10px] tracking-[0.2em] text-ink-soft">Email</label>
                                <input
                                    type="email"
                                    value={su.email}
                                    onChange={(e) => setSu({ ...su, email: e.target.value })}
                                    required
                                    className="w-full border border-ink/30 bg-paper-light px-3 py-2 font-body text-sm text-ink"
                                    data-testid="signup-email"
                                />
                            </div>
                            <div>
                                <label className="label-typed text-[10px] tracking-[0.2em] text-ink-soft">Password (min 8)</label>
                                <input
                                    type="password"
                                    value={su.password}
                                    onChange={(e) => setSu({ ...su, password: e.target.value })}
                                    required minLength={8}
                                    className="w-full border border-ink/30 bg-paper-light px-3 py-2 font-body text-sm text-ink"
                                    data-testid="signup-password"
                                />
                            </div>
                            <div>
                                <label className="label-typed text-[10px] tracking-[0.2em] text-ink-soft">Capacity</label>
                                <div className="flex gap-2 mt-1">
                                    <label className="flex-1 border border-ink/30 px-2 py-1.5 text-xs cursor-pointer flex items-center gap-2 hover:border-stamp-red">
                                        <input
                                            type="radio"
                                            name="intent"
                                            value="test_user"
                                            checked={su.intent === "test_user"}
                                            onChange={() => setSu({ ...su, intent: "test_user" })}
                                            data-testid="signup-intent-test"
                                        /> Test operative
                                    </label>
                                    <label className="flex-1 border border-ink/30 px-2 py-1.5 text-xs cursor-pointer flex items-center gap-2 hover:border-stamp-red">
                                        <input
                                            type="radio"
                                            name="intent"
                                            value="launch_user"
                                            checked={su.intent === "launch_user"}
                                            onChange={() => setSu({ ...su, intent: "launch_user" })}
                                            data-testid="signup-intent-launch"
                                        /> Launch operative
                                    </label>
                                </div>
                            </div>
                            {suErr && (
                                <div className="border border-stamp-red/40 bg-stamp-red/5 text-stamp-red text-xs px-2 py-1.5" data-testid="signup-error">
                                    {suErr}
                                </div>
                            )}
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => setSignupOpen(false)}
                                    className="border border-ink/30 px-3 py-2 font-heading uppercase tracking-[0.22em] text-xs text-ink hover:border-stamp-red"
                                    data-testid="signup-cancel"
                                >Close</button>
                                <button
                                    type="submit"
                                    disabled={suBusy}
                                    className="flex-1 bg-stamp-red text-paper-light font-heading uppercase tracking-[0.22em] text-xs py-2 hover:bg-ink disabled:opacity-40"
                                    data-testid="signup-submit"
                                >{suBusy ? "Filing…" : "Register"}</button>
                            </div>
                        </form>
                    )}
                </div>
                )}
                <div className="mt-6 pt-4 border-t border-ink/15 flex gap-4 text-[10px] font-body text-ink-soft uppercase tracking-[0.18em] flex-wrap items-center">
                    <a href="/privacy" className="hover:text-stamp-red" data-testid="login-privacy-link">Privacy</a>
                    <a href="/terms" className="hover:text-stamp-red" data-testid="login-terms-link">Terms</a>
                    <a href="/upgrade" className="hover:text-stamp-red" data-testid="login-upgrade-link">Recruitment</a>
                    <span className="opacity-30">·</span>
                    <BuildBadge />
                </div>
                {/* Iter 31.215 — agent-curated footer tagline. Invisible
                    if no egg is live. */}
                <EasterEggSlot
                    path="login"
                    slot="login.footer_tagline"
                    as="div"
                    className="mt-3 text-[10px] font-body text-ink-soft italic tracking-[0.14em] opacity-70"
                />
            </div>
            {/* Iter 31.211 — Public Spy Radio panel beside the auth box. */}
            <PublicRadioPanel />
            </div>
        </div>
    );
}
